import React from 'react';
import ReactMarkdown from 'react-markdown';

import { useAppContext } from '@/hooks/use-app-context';

/**
 * 空間情報表示UIコンポーネントのプロパティ
 */
export interface InfoPanelProps {
  /** 表示するかどうか */
  visible: boolean;
  /** 閉じるボタンがクリックされたときのコールバック */
  onClose?: () => void;
}

/**
 * 空間情報表示UIコンポーネント
 *
 * 左下に表示される空間情報パネル。infoボタンを押したら表示されて、×で消える。
 */
export function InfoPanel({ visible, onClose }: InfoPanelProps) {
  const { getCurrentRoom, getCurrentHistory } = useAppContext();

  const currentRoom = getCurrentRoom();
  const currentHistory = getCurrentHistory();

  // 固定スタイル設定
  const containerWidth = '375px';
  const containerLeft = '88px';
  const containerBottom = '32px';
  const containerBorderRadius = '16px';

  const headerPadding = '10px 15px';
  const headerHeight = '44px';

  const closeButtonFontSize = '20px';
  const closeButtonLeft = '15px';

  const titleFontSize = '20px';

  const contentPadding = '24px';
  const dateFontSize = '14px';
  const descriptionFontSize = '14px';

  if (!visible || !currentRoom || !currentHistory) {
    return null;
  }

  // 情報パネルを閉じる
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: containerBottom,
        left: containerLeft,
        width: containerWidth,
        backgroundColor: 'white',
        borderRadius: containerBorderRadius,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden',
        fontFamily: 'sans-serif',
        zIndex: 1002,
        opacity: 0.9,
        minHeight: '246px', // 最低限の高さを設定
        maxHeight: 'calc(100vh - 340px)' // ウィンドウの高さから余白を引いた最大高さを設定
      }}
    >
      <div
        style={{
          position: 'relative',
          padding: headerPadding,
          borderBottom: '1px solid #eee',
          height: headerHeight // 動的な高さを設定
        }}
      >
        <button
          style={{
            position: 'absolute',
            left: closeButtonLeft,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            fontSize: closeButtonFontSize,
            cursor: 'pointer',
            padding: '0 5px',
            zIndex: 2,
            color: '#000000'
          }}
          onClick={handleClose}
        >
          ×
        </button>
        <h3
          title={currentRoom.name} // ホバー時にツールチップで全文表示
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            margin: 0,
            fontSize: titleFontSize,
            fontWeight: 'bold',
            textAlign: 'center',
            zIndex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 48px',
            color: '#000000'
          }}
        >
          {currentRoom.name}
        </h3>
      </div>
      <div
        style={{
          padding: contentPadding,
          overflowY: 'auto', // 縦方向のスクロールを有効化
          maxHeight: `calc(100vh - 340px - ${headerHeight})` // コンテナの最大高さからヘッダー高さを引いた値
        }}
      >
        <div style={{ marginBottom: '24px', fontSize: dateFontSize, color: '#000000' }}>{currentHistory.date}</div>
        <div
          style={{
            fontSize: descriptionFontSize,
            lineHeight: '1.5',
            color: '#000000'
          }}
        >
          <ReactMarkdown
            components={{
              a: ({ ...props }) => (
                <a
                  {...props}
                  rel='noopener noreferrer'
                  style={{ color: '#0066cc', textDecoration: 'underline' }}
                  target='_blank'
                />
              ),
              p: ({ children, ...props }) => (
                <p {...props} style={{ margin: '0 0 12px 0' }}>
                  {children}
                </p>
              )
            }}
          >
            {currentHistory.description}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
